import {
	useMutation,
	useQueryClient,
	useSuspenseQuery,
} from "@tanstack/react-query";
import { type FC, Suspense, useCallback, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import type { MetaAdsComparisonKPIs } from "shared";
import { Api } from "../api.config";
import { AlertType, useAlertEmit } from "../common/alert/Alert";
import { Icon } from "../common/Icon";
import { useCompany } from "../company/useCompany";
import { Card } from "../components/Card";
import { AppPage } from "../layout/AppLayout";
import { CampaignTable } from "./components/CampaignTable";
import {
	DateRangeSelector,
	getDefaultDateRange,
	persistDateRange,
} from "./components/DateRangeSelector";
import { DisapprovedAdsBanner } from "./components/DisapprovedAdsBanner";
import { DisapprovedAdsTable } from "./components/DisapprovedAdsTable";
import { InsightsTrendChart } from "./components/InsightsTrendChart";
import { KPISummaryCards } from "./components/KPISummaryCards";
import { useMetaAdsDashboard } from "./useMetaAdsReporting";
import { useCanEditMetaAds } from "./useMyMetaAdsRole";

const DashboardContent: FC<{
	from: string;
	to: string;
	comparison?: MetaAdsComparisonKPIs;
}> = ({ from, to, comparison }) => {
	const { data } = useMetaAdsDashboard(from, to);

	return (
		<>
			<KPISummaryCards kpis={data.kpis} changes={comparison?.changes} />
			<InsightsTrendChart data={data.trend} />
		</>
	);
};

const ComparisonDashboard: FC<{
	from: string;
	to: string;
	compareFrom: string;
	compareTo: string;
}> = ({ from, to, compareFrom, compareTo }) => {
	const company = useCompany();
	const { data: comparison } = useSuspenseQuery({
		queryKey: [
			"COMPANY",
			company.id,
			"INTEGRATIONS",
			"META_ADS",
			"REPORTING",
			"COMPARISON",
			from,
			to,
			compareFrom,
			compareTo,
		],
		queryFn: () =>
			Api.GetMetaAdsDashboardComparison(from, to, compareFrom, compareTo).then(
				(r) => r.data,
			),
	});

	const { data } = useMetaAdsDashboard(from, to);

	return (
		<>
			<KPISummaryCards kpis={data.kpis} changes={comparison.changes} />
			<InsightsTrendChart data={data.trend} />
		</>
	);
};

export const PageMetaAdsReporting: FC = () => {
	const company = useCompany();
	const queryClient = useQueryClient();
	const emit = useAlertEmit();
	const canEdit = useCanEditMetaAds();

	const [dateRange, setDateRange] = useState(getDefaultDateRange);
	const [showDisapproved, setShowDisapproved] = useState(false);
	const [compareEnabled, setCompareEnabled] = useState(false);
	const [compareRange, setCompareRange] = useState(() => {
		const def = getDefaultDateRange();
		const fromDate = new Date(def.from);
		const toDate = new Date(def.to);
		const diff = toDate.getTime() - fromDate.getTime();
		const prevTo = new Date(fromDate.getTime() - 86400000);
		const prevFrom = new Date(prevTo.getTime() - diff);
		return {
			from: prevFrom.toISOString().split("T")[0],
			to: prevTo.toISOString().split("T")[0],
		};
	});

	const handleDateChange = useCallback((from: string, to: string) => {
		persistDateRange(from, to);
		setDateRange({ from, to });
		// Auto-compute comparison range
		const fromDate = new Date(from);
		const toDate = new Date(to);
		const diff = toDate.getTime() - fromDate.getTime();
		const prevTo = new Date(fromDate.getTime() - 86400000);
		const prevFrom = new Date(prevTo.getTime() - diff);
		setCompareRange({
			from: prevFrom.toISOString().split("T")[0],
			to: prevTo.toISOString().split("T")[0],
		});
	}, []);

	const syncMutation = useMutation({
		mutationFn: () => Api.SyncMetaAdsInsights().then((r) => r.data),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: [
					"COMPANY",
					company.id,
					"INTEGRATIONS",
					"META_ADS",
					"REPORTING",
				],
			});
			emit({
				type: AlertType.SUCCESS,
				content: `Synced ${data.syncedCount} insight rows.`,
			});
		},
		onError: () => {
			emit({
				type: AlertType.ERROR,
				content: "Failed to sync insights.",
			});
		},
	});

	const downloadMutation = useMutation({
		mutationFn: () =>
			Api.GenerateMetaAdsReport({
				from: dateRange.from,
				to: dateRange.to,
				compareFrom: compareEnabled ? compareRange.from : undefined,
				compareTo: compareEnabled ? compareRange.to : undefined,
			}).then((r) => r.data),
		onSuccess: (blob) => {
			const url = URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `meta-ads-report-${dateRange.from}-to-${dateRange.to}.pdf`;
			a.click();
			URL.revokeObjectURL(url);
		},
		onError: () => {
			emit({ type: AlertType.ERROR, content: "Failed to generate PDF." });
		},
	});

	return (
		<AppPage title="Meta Ads Reporting">
			<div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
				<div
					style={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						flexWrap: "wrap",
						gap: 12,
					}}
				>
					<DateRangeSelector
						from={dateRange.from}
						to={dateRange.to}
						onChange={handleDateChange}
					/>
					<div style={{ display: "flex", gap: 8, alignItems: "center" }}>
						<button
							type="button"
							className={`btn btn-sm ${compareEnabled ? "btn-primary" : "btn-outline-secondary"}`}
							onClick={() => setCompareEnabled((v) => !v)}
						>
							<Icon name="compare_arrows" size={16} />{" "}
							{compareEnabled ? "Comparing" : "Compare"}
						</button>
						{canEdit && (
							<button
								type="button"
								className="btn btn-sm btn-outline-primary"
								disabled={downloadMutation.isPending}
								onClick={() => downloadMutation.mutate()}
							>
								<Icon name="download" size={16} />{" "}
								{downloadMutation.isPending ? "Generating..." : "Download PDF"}
							</button>
						)}
						{canEdit && (
							<button
								type="button"
								className="btn btn-sm btn-outline-primary"
								disabled={syncMutation.isPending}
								onClick={() => syncMutation.mutate()}
							>
								<Icon name="sync" size={16} />{" "}
								{syncMutation.isPending ? "Syncing..." : "Refresh Data"}
							</button>
						)}
					</div>
				</div>

				{compareEnabled && (
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: 8,
							fontSize: "0.85rem",
							color: "var(--bs-secondary)",
						}}
					>
						<span>Comparing with:</span>
						<input
							type="date"
							className="form-control form-control-sm"
							style={{ width: "auto" }}
							value={compareRange.from}
							onChange={(e) =>
								setCompareRange((r) => ({ ...r, from: e.target.value }))
							}
						/>
						<span>to</span>
						<input
							type="date"
							className="form-control form-control-sm"
							style={{ width: "auto" }}
							value={compareRange.to}
							onChange={(e) =>
								setCompareRange((r) => ({ ...r, to: e.target.value }))
							}
						/>
					</div>
				)}

				<DisapprovedAdsBanner
					onShowDisapproved={() => setShowDisapproved(true)}
				/>

				<ErrorBoundary
					fallback={
						<Card>
							<Card.Body>
								<p className="text-muted" style={{ marginBottom: 0 }}>
									Unable to load dashboard data. Try syncing first.
								</p>
							</Card.Body>
						</Card>
					}
				>
					<Suspense fallback={<p>Loading dashboard...</p>}>
						{compareEnabled ? (
							<ComparisonDashboard
								from={dateRange.from}
								to={dateRange.to}
								compareFrom={compareRange.from}
								compareTo={compareRange.to}
							/>
						) : (
							<DashboardContent from={dateRange.from} to={dateRange.to} />
						)}
					</Suspense>
				</ErrorBoundary>

				<ErrorBoundary
					fallback={
						<Card>
							<Card.Body>
								<p className="text-muted" style={{ marginBottom: 0 }}>
									Unable to load campaign data.
								</p>
							</Card.Body>
						</Card>
					}
				>
					{showDisapproved ? (
						<>
							<div>
								<button
									type="button"
									className="btn btn-sm btn-outline-secondary"
									onClick={() => setShowDisapproved(false)}
								>
									Back to Campaigns
								</button>
							</div>
							<DisapprovedAdsTable />
						</>
					) : (
						<CampaignTable
							from={dateRange.from}
							to={dateRange.to}
							showStatusToggle={canEdit}
						/>
					)}
				</ErrorBoundary>
			</div>
		</AppPage>
	);
};
